export interface VocalPack {
  name: string;
  description: string;
  styles: string;
  excludeStyles: string;
  voiceLead: string;
  voiceAltA?: string;
  voiceAltB?: string;
  harmonyStack: string;
  gangVocals: string;
  mixTargets: string;
  physics: string;
}

export interface DerivedTraits {
  grit: number;
  rasp: number;
  projection: number;
  vibrato: number;
  airiness: number;
  nasality: number;
  lowEnd: number;
  speed: number;
  cleanness: number;
}

export interface PacingProfile {
  id: string;
  label: string;
  bpmRange: [number, number];
  breathingCues: string;
  phraseLength: string;
  syllableDensity: string;
  restBehavior: string;
  dynamicRange: string;
}

export interface BlendConfig {
  packAKey: string | null;
  packBKey: string | null;
  blendRatio: number;
  tempo: number;
}

export interface ScreamConfiguration {
  typeId: string | null;
  intensityId: string;
}

export interface DialectModifier {
  id: string;
  label: string;
  region: string;
  description: string;
  traitModifiers: Partial<DerivedTraits>;
  physicsPrompt: string;
  sunoTags: string[];
  excludeTags: string[];
}

export interface AgingProfile {
  id: string;
  label: string;
  ageRange: [number, number];
  description: string;
  traitModifiers: Partial<DerivedTraits>;
  physicsPrompt: string;
  sunoTags: string[];
}

export interface BreathPattern {
  id: string;
  label: string;
  description: string;
  breathingCues: string;
  physicsPrompt: string;
  sunoTags: string[];
  bestFor: string[];
}

export interface EmotionalArcPoint {
  position: number;
  emotion: string;
  intensity: number;
}

export interface EmotionalArc {
  id: string;
  label: string;
  description: string;
  points: EmotionalArcPoint[];
  physicsPrompt: string;
  sunoTags: string[];
}

export interface DeepDiveConfiguration {
  dialectId: string | null;
  agingId: string | null;
  breathPatternId: string | null;
  emotionalArcId: string | null;
  customArc: EmotionalArcPoint[] | null;
}

export interface BlendedVocalOutput {
  traits: DerivedTraits;
  name: string;
  description: string;
  styles: string;
  excludeStyles: string;
  voiceLead: string;
  harmonyStack: string;
  gangVocals: string;
  mixTargets: string;
  physics: string;
  pacing: PacingProfile;
  fullPromptBlock: string;
  compactPrompt: string;
  sunoStyleTags: string[];
  screamPhysics?: string;
  deepDivePhysics?: string;
}

export interface VocalConfiguration {
  mode: 'single' | 'blend';
  singlePackKey: string | null;
  voiceSpec: 'lead' | 'altA' | 'altB';
  includeHarmonyStack: boolean;
  includeChoirGang: boolean;
  blendConfig: BlendConfig | null;
  blendOutput: BlendedVocalOutput | null;
  scream: ScreamConfiguration;
  deepDive: DeepDiveConfiguration;
}

export interface HookhouseFormData {
  seed: string;
  blends: string[];
  stylePreset: string;
  vocalPackKey: string;
  selectedVoiceSpec: 'Lead' | 'AltA' | 'AltB';
  includeHarmonyStack: boolean;
  includeGangVocals: boolean;
  isInstrumental: boolean;
  mood: string[];
  explicit: boolean;
  povSetting: string;
  themesToHit: string;
  themesToAvoid: string;
  instruments: string;
  constraints: string;
  styleStrength: number;
  weirdness: number;
  songStructure: StructureConfig;
  rhymeScheme: RhymeConfig;
  /**
   * Suno treats ANY appearance of the word "live" in style/tags as an
   * instruction to produce a live-concert performance (crowd noise, audience
   * atmosphere) -- even innocuous studio-engineering uses like "live room
   * feel". 'studio' forbids "live" language entirely (rephrasing it when it
   * would otherwise appear); 'live' explicitly wants the concert character.
   * Defaults to 'studio' when unset.
   */
  styleMode?: 'studio' | 'live';
}

/**
 * Rhyme and structure — decomposed per docs/Rhyme-Schemes.md and
 * docs/Song-Structures.md (v2). The old SONG_STRUCTURES/RHYME_SCHEMES flat
 * lists mixed incompatible axes into single options (structure mixed
 * architecture with modifiers; rhyme mixed scheme/exactness/cadence/devices/
 * meter), so most selections didn't actually answer "which lines rhyme" or
 * "what's the section order" and silently fell back to the same handful of
 * defaults (Forgejo #90). Each axis is now an independent field; a value of
 * 'default' on any field omits that field's instruction line entirely
 * (unchanged behavior for a form nobody has touched).
 */
export interface RhymeOption { value: string; label: string; description: string; }

export const RHYME_SCHEME_OPTIONS: RhymeOption[] = [
  { value: 'default', label: 'Default (AI Decides)', description: 'No scheme instruction — let the model choose' },
  { value: 'AABB couplets: lines 1&2 rhyme, lines 3&4 rhyme. End-rhyme matches from the final stressed syllable onward.', label: 'S1 — AABB Couplets', description: 'Closed, resolved, sing-song. Country, folk' },
  { value: 'ABAB alternate rhyme: lines 1&3 rhyme, lines 2&4 rhyme. Avoid forced end-words.', label: 'S2 — ABAB Alternate', description: 'Woven, forward-leaning, less final than couplets' },
  { value: 'ABCB ballad rhyme: only lines 2&4 rhyme; lines 1&3 are unrhymed. Storytelling feel.', label: 'S3 — ABCB Ballad', description: 'Loose, spoken, narrative. Lines 1&3 run free' },
  { value: 'AABA: lines 1, 2 and 4 share a rhyme; line 3 breaks free and does not rhyme.', label: 'S4 — AABA Enclosed Break', description: 'Line 3 lands as a departure — a built-in turn' },
  { value: 'ABBA enclosed rhyme: outer lines 1&4 rhyme, inner lines 2&3 rhyme.', label: 'S5 — ABBA Enclosed', description: 'Inward, literary, self-contained. Frames a thought' },
  { value: 'AAB blues stanza: line 1 states, line 2 repeats line 1 (verbatim or near), line 3 answers with a rhyming resolution.', label: 'S6 — AAB Blues Stanza', description: 'The 12-bar lyric form. Statement, restatement, punchline' },
  { value: 'AAAA monorhyme: all line endings in the stanza share one rhyme. Vary syntax and imagery to avoid monotony.', label: 'S7 — AAAA Monorhyme', description: 'Hypnotic, relentless, incantatory' },
  { value: 'AAA tercets: each 3-line stanza uses a single repeated rhyme. Vary imagery.', label: 'S8 — AAA Triplet', description: 'Tight, punchy, compressed' },
  { value: 'AAAB: lines 1, 2 and 3 share a rhyme; line 4 breaks free and lands unrhymed.', label: 'S9 — AAAB Triple-and-Break', description: 'Three-beat build then a drop. Strong for pre-chorus' },
  { value: 'AABCCB tail rhyme (6 lines): lines 1&2 rhyme, 4&5 rhyme, and lines 3&6 rhyme with each other.', label: 'S10 — AABCCB Tail Rhyme', description: 'Hymn and folk stanza. Two couplets pinned by a long line' },
  { value: 'ABABCC: alternate-rhymed quatrain followed by a rhyming couplet that closes the thought.', label: 'S11 — ABABCC Quatrain+Couplet', description: 'A verse that ends by snapping shut. Chorus-adjacent punch' },
  { value: 'Terza rima: interlocking tercets ABA BCB CDC DED. Each middle rhyme becomes the outer rhyme of the next stanza.', label: 'S12 — Terza Rima Chain', description: 'Interlocking. Each stanza pulls the next in' },
  { value: 'Free verse: no fixed rhyme scheme. Let meaning and breath drive line breaks.', label: 'S0 — Free Verse', description: 'Meaning drives line breaks. Spoken word' },
];

export const RHYME_EXACTNESS_OPTIONS: RhymeOption[] = [
  { value: 'default', label: 'Mixed (default)', description: 'Perfect on hook/chorus lines, slant through verses' },
  { value: 'Perfect rhyme throughout: identical sounds from the final stressed vowel onward. Never repeat the same word as its own rhyme.', label: 'X1 — Perfect', description: 'Crisp and clean everywhere' },
  { value: 'Slant rhyme throughout: approximate near-rhymes sharing vowel or consonant but not both (shape/keep, soul/all).', label: 'X2 — Slant', description: 'Approximate near-rhymes, alt-country/indie feel' },
];

export const RHYME_CADENCE_OPTIONS: RhymeOption[] = [
  { value: 'default', label: 'Mixed (default)', description: 'Masculine on hook/chorus, feminine permitted in verses' },
  { value: 'Masculine rhyme throughout: stress on the final syllable (delay/away, ignite/tonight). Punchy, uncluttered line endings.', label: 'C1 — Masculine', description: 'Punchy, uncluttered endings' },
  { value: 'Feminine rhyme throughout: stress on the penultimate syllable (falling/calling, power/tower). Softer, trailing endings.', label: 'C2 — Feminine', description: 'Softer, trailing endings' },
];

export const RHYME_DEVICE_OPTIONS: RhymeOption[] = [
  { value: 'Internal rhyme within lines, complementing end-rhyme. Maintain natural readability.', label: 'D1 — Internal Rhyme', description: 'Rhymes inside lines, not just at ends' },
  { value: 'Repeat vowel sounds across line endings and stressed positions for texture (assonance).', label: 'D2 — Assonance', description: 'Vowel echoes for texture' },
  { value: 'Repeat consonant sounds in stressed positions (brick/break) for rhythmic texture (consonance).', label: 'D3 — Consonance', description: 'Consonant echoes, rhythmic texture' },
  { value: 'Repeat initial sounds at line starts for momentum (alliteration). Clarity over tongue-twisters.', label: 'D4 — Alliteration', description: 'Driving momentum at line starts' },
  { value: 'Feature s/sh/z sounds in rhyme positions for hissed, venomous texture. Use sparingly.', label: 'D5 — Sibilant Loading', description: 'Hissed, venomous texture' },
];

export const RHYME_METER_OPTIONS: RhymeOption[] = [
  { value: 'default', label: 'Speech-Rhythm (default)', description: 'No fixed foot — natural spoken stress' },
  { value: 'Iambic meter: unstressed followed by stressed. The default pulse of English.', label: 'M1 — Iambic', description: 'da-DUM — the default pulse of English' },
  { value: 'Trochaic meter: stressed followed by unstressed. Insistent, chant-like, front-loaded.', label: 'M2 — Trochaic', description: 'DUM-da — insistent, chant-like' },
  { value: 'Anapestic meter: two unstressed syllables followed by a stressed syllable. Galloping, driving feel.', label: 'M3 — Anapestic', description: 'da-da-DUM — galloping, driving' },
  { value: 'Dactylic meter: stressed syllable followed by two unstressed. Rolling, waltz-like feel.', label: 'M4 — Dactylic', description: 'DUM-da-da — rolling, waltz-like' },
];

export interface RhymeConfig {
  verseScheme: string;
  chorusScheme: string;
  bridgeScheme: string;
  exactness: string;
  cadence: string;
  /** Up to 2 values from RHYME_DEVICE_OPTIONS. */
  devices: string[];
  meter: string;
}

export const DEFAULT_RHYME_CONFIG: RhymeConfig = {
  verseScheme: 'default',
  chorusScheme: 'default',
  bridgeScheme: 'default',
  exactness: 'default',
  cadence: 'default',
  devices: [],
  meter: 'default',
};

export interface StructureOption { value: string; label: string; description: string; }

export const STRUCTURE_ARCHITECTURE_OPTIONS: StructureOption[] = [
  { value: 'default', label: 'Default (AI Decides)', description: 'No architecture instruction — let the model choose' },
  { value: 'Intro, Verse, Chorus, Verse, Chorus, Bridge, Chorus, Outro. The baseline: chorus arrives early and returns on schedule; the bridge supplies the one departure.', label: 'A1 — Verse-Chorus', description: 'The baseline. Hook working by 0:45' },
  { value: 'Verse, Verse, [Verse], Chorus, Verse, Chorus. Two or three verses establish the story before the chorus is revealed as payoff, not refrain.', label: 'A2 — Deferred Chorus', description: 'The chorus IS the reveal' },
  { value: 'Hook, Verse, Hook, Verse, Bridge, Hook. The hook (not a full chorus) is the recurring structural unit — short, repeated, may be the only repeated material.', label: 'A3 — Hook-Anchored', description: 'One line carries the whole song' },
  { value: 'Chorus, Verse, Chorus, Verse, Chorus. Opens cold on the chorus; the listener knows the destination from bar one, verses explain how you got there.', label: 'A4 — Chorus-First', description: 'Opens on the hook, verses justify it' },
  { value: 'Verse+refrain, Verse+refrain, Verse+refrain, [Verse+refrain]. Same music every stanza, no chorus, no bridge — the oldest form in Anglo-American song.', label: 'A5 — Strophic', description: 'Narrative is the whole job' },
  { value: 'A, A, B, A (32-bar Tin Pan Alley form). Two statements of the main strain, one contrasting bridge, return.', label: 'A6 — AABA (32-Bar)', description: "A standard's shape — compact, melodic" },
  { value: 'Blues cycle: 12- or 16-bar harmonic cycle repeated, lyric form AAB per cycle. The changes carry the identity, not verse/chorus labels.', label: 'A7 — Blues Cycle', description: 'For blues and blues-derived rock' },
  { value: 'Through-composed: A, B, C, D, E — no repeated section, continuous narrative. Needs a strong recurring melodic or lyric motif even though no section repeats.', label: 'A8 — Through-Composed', description: 'Linear, irreversible story' },
  { value: 'Build-Drop: Intro, Build, Drop, Verse, Build, Drop, Bridge, Drop, Outro. Tension-and-release cycles replace verse-chorus; the drop is the payoff.', label: 'A9 — Build-Drop', description: 'Electronic/hybrid, energy contour over lyric form' },
  { value: 'Suite: two or more architectures concatenated as movements, usually with tempo or meter changes at the seams.', label: 'A10 — Suite (Multi-Part)', description: 'Genuinely long-form, one shape cannot hold it' },
];

export const STRUCTURE_MODIFIER_OPTIONS: StructureOption[] = [
  { value: 'a lead instrumental solo section, usually before the final chorus', label: '+SOLO — Instrumental Solo', description: 'A lead voice takes a section' },
  { value: 'a full arranged instrumental passage (not a solo over changes)', label: '+INST — Instrumental Section', description: 'A full instrumental passage' },
  { value: 'a breakdown stripping to one or two elements — the floor drops out', label: '+BREAK — Breakdown', description: 'Strip to one or two elements' },
  { value: 'a pre-chorus lift section between verse and chorus', label: '+PRE — Pre-Chorus', description: 'A lift section before the chorus' },
  { value: 'a key change / modulation before the final section', label: '+KEY — Key Change', description: 'Modulation before the final section' },
  { value: 'two verses before the first chorus (double open)', label: '+DBL — Double Open', description: 'Two verses before first chorus' },
  { value: 'a spoken-word intro or mid-song interlude', label: '+SPOKEN — Spoken Passage', description: 'Spoken word intro or interlude' },
  { value: 'a repeated closing tag/vamp phrase over a held progression', label: '+TAG — Tag / Vamp', description: 'A repeated closing phrase' },
  { value: 'an a cappella opening — voice alone before the band enters', label: '+ACAP — A Cappella Open', description: 'Voice alone before the band enters' },
  { value: 'a false ending (apparent stop, then the song returns)', label: '+FALSE — False Ending', description: 'Apparent stop, then return' },
  { value: 'a reprise — earlier material returns transformed late in the song', label: '+REPRISE — Reprise', description: 'Earlier material returns transformed' },
];

export const STRUCTURE_ENDING_OPTIONS: StructureOption[] = [
  { value: 'default', label: 'Default (AI Decides)', description: 'No ending instruction — let the model choose' },
  { value: 'Cold stop: the band stops together. Decisive, confrontational.', label: 'E1 — Cold Stop', description: 'Decisive, confrontational' },
  { value: 'Fade out, implying continuation beyond the frame. Vintage.', label: 'E2 — Fade Out', description: 'Implies continuation. Vintage' },
  { value: 'Tag/vamp repeat: the refrain cycles and decays. Communal, gospel-adjacent.', label: 'E3 — Tag / Vamp Repeat', description: 'Refrain cycles and decays' },
  { value: 'False ending that stops, then returns for a reprise. Theatrical.', label: 'E4 — False Ending → Reprise', description: 'Stops, then returns. Theatrical' },
  { value: 'Ritard / decay: the song slows and dissolves. Exhausted, resigned.', label: 'E5 — Ritard / Decay', description: 'Slows and dissolves' },
  { value: 'Unresolved ending: off the tonic or mid-phrase. Unsettled, Gothic.', label: 'E6 — Unresolved', description: 'Off the tonic or mid-phrase' },
];

export interface StructureConfig {
  architecture: string;
  /** Up to 2 values from STRUCTURE_MODIFIER_OPTIONS. */
  modifiers: string[];
  ending: string;
}

export const DEFAULT_STRUCTURE_CONFIG: StructureConfig = {
  architecture: 'default',
  modifiers: [],
  ending: 'default',
};

export interface ParsedSunoOutput {
  titleArtist: string;
  summary: string;
  styleBlueprint: string;
  style: string;
  excludedStyle: string;
  title: string;
  artist: string;
  lyricsWithTags: string;
  lyricsAlone: string;
  javascriptCode: string;
  fullResponse: string;
  styleStrength?: number;
  weirdness?: number;
  llmModel?: string;
  llmProvider?: string;
  charLimits?: {
    styleTags: { count: number; max: number; over: boolean };
    excludedStyles: { count: number; max: number; over: boolean };
    lyrics: { count: number; max: number; over: boolean };
  };
  /** Forgejo #106 -- which DraftVault row this output corresponds to, when
   *  known (e.g. loaded from the Vault). Lets App.tsx's onSelectOutput wire
   *  up editing for output that didn't come from a fresh generation. */
  draftId?: string | null;
}

export interface AlignedWord {
  word: string;
  start: number;
  end: number;
}

export interface SunoClip {
  id: string;
  title: string;
  created_at: string;
  model_name: string;
  imageUrl?: string;
  imageLargeUrl?: string;
  metadata: {
    tags: string | null;
    prompt: string | null;
  };
  originalData?: ParsedSunoOutput;
  alignmentData?: AlignedWord[];
  lrcContent?: string;
  srtContent?: string;
  status?: string;
  audio_url?: string;
  batch_index?: number;
  video_url?: string;
  video_status?: string;
  draftId?: string | null;
  is_public?: boolean;
}

export interface MusicCritique {
  songId: string;
  timestamp: string;
  overallScore: number;
  sections: {
    lyricalAnalysis: {
      score: number;
      feedback: string;
      strengths: string[];
      weaknesses: string[];
    };
    musicalArrangement: {
      score: number;
      feedback: string;
      instrumentationReview: string;
      grooveAnalysis: string;
    };
    production: {
      score: number;
      feedback: string;
      mixQuality: string;
      sonicClarity: string;
    };
    genreAuthenticity: {
      score: number;
      feedback: string;
      southernRockElements: string[];
      authenticityConcerns: string[];
    };
  };
  improvementSuggestions: string[];
  summaryVerdict: string;
}

export interface LiveLocation {
  name: string;
  prompt: string;
}

export interface SunoDirectExport {
  styleTags: string;
  excludeTags: string;
  physicsBlock: string;
  tagsList: string[];
  charCounts: { styleTags: number; excludeTags: number; physicsBlock: number };
  isOverLimit: boolean;
}

export interface BlendOption {
  label: string;
  description: string;
}

export interface BlendGroup {
  name: string;
  options: BlendOption[];
}

export interface MoodOption {
  label: string;
  description: string;
}

export interface MoodGroup {
  name: string;
  options: MoodOption[];
}

export const QUICK_BLEND_GROUPS: BlendGroup[] = [
  {
    name: "Heavy/Rock Styles",
    options: [
      { label: "Southern Rock", description: "Classic twin-guitar interplay and bluesy drive" },
      { label: "Stoner Rock", description: "Desert rock fuzz, Kyuss/QOTSA vibes" },
      { label: "Sludge", description: "Eyehategod/Crowbar swamp metal" },
      { label: "Doom Metal", description: "Slow, heavy, and ominous Sabbath-style riffs" },
      { label: "Heavy Metal", description: "Iron Maiden/Judas Priest twin-lead gallop" },
      { label: "Glam Metal", description: "80s Sunset Strip hair metal, chorused guitars" },
      { label: "Blues Rock", description: "Cream/Hendrix classic blues influence" },
      { label: "Classic Rock", description: "70s warmth and stadium-sized energy" },
      { label: "Cowpunk", description: "Social Distortion meets Hank III speed" }
    ]
  },
  {
    name: "Extreme & Modern Metal",
    options: [
      { label: "Groove Metal", description: "Pantera-style chugging rhythms and stomp" },
      { label: "Power Metal", description: "Soaring vocals, fast double bass, epic melodies" },
      { label: "Black Metal", description: "Tremolo picking, blast beats, atmospheric cold" },
      { label: "Death Metal", description: "Guttural vocals, technical riffs, brutal precision" },
      { label: "Progressive Metal", description: "Dream Theater odd meters and complex arrangements" },
      { label: "Nu-Metal", description: "Down-tuned riffs, turntables, rap-rock aggression" },
      { label: "Djent", description: "Meshuggah-style polyrhythmic palm-muted staccato" },
      { label: "Viking Metal", description: "Norse folk melodies fused with black/death metal fury" }
    ]
  },
  {
    name: "Southern & Roots",
    options: [
      { label: "Swamp Rock", description: "CCR bayou groove and murky textures" },
      { label: "Texas Blues", description: "SRV grit and aggressive shuffle feel" },
      { label: "Heartland Rock", description: "Petty/Mellencamp blue-collar anthems" },
      { label: "Bluegrass", description: "Fast pickin' acoustic roots" },
      { label: "Honky Tonk", description: "Classic country barroom, piano-led" },
      { label: "Boogie", description: "ZZ Top style driving 12-bar rhythm" },
      { label: "Americana", description: "Rootsy blend of folk, country, and rock" },
      { label: "Jam", description: "Improvisational flow, Dead/Panic vibes" }
    ]
  },
  {
    name: "Groove & Soul",
    options: [
      { label: "Groove", description: "Focus on rhythmic 'pocket' and bass/drum lock" },
      { label: "Funk", description: "Percussive scratchy guitars and syncopation" },
      { label: "Psychedelic Funk", description: "Parliament/Funkadelic cosmic grooves and synth bass" },
      { label: "Gospel", description: "Organ swells and choral-backed soulful power" },
      { label: "Soul", description: "Deep feeling, emotive vocals, and brass stabs" },
      { label: "Philadelphia Soul", description: "Lush orchestral strings, silky harmonies, TSOP polish" },
      { label: "Stax Soul", description: "Memphis grit, raw horns, Booker T organ crunch" },
      { label: "Southern Soul", description: "Muscle Shoals warmth, deep groove, earthy vocals" },
      { label: "Neo-Soul", description: "Erykah Badu/D'Angelo organic warmth, jazzy chord voicings" },
      { label: "Quiet Storm", description: "Late-night silky slow jams, Luther Vandross intimacy" },
      { label: "Jazz Fusion", description: "Complex harmony, improvisation, Mahavishnu/Weather Report" }
    ]
  },
  {
    name: "Country & Alt-Country",
    options: [
      { label: "Nashville Country", description: "Polished Music City commercial studio sound" },
      { label: "Modern Country", description: "2020s radio-ready polished country" },
      { label: "Bro-Country", description: "Party anthems, tailgate energy, pop-rock hooks" },
      { label: "Alt-Country", description: "Wilco/Uncle Tupelo indie roots-rock crossover" },
      { label: "Bakersfield Sound", description: "Buck Owens/Merle Haggard twangy electric honky tonk" },
      { label: "Contemporary Country", description: "Chris Stapleton-era authentic modern country" },
      { label: "Country Rock", description: "Eagles/Poco country-meets-rock harmony blend" },
      { label: "Mainstream Country", description: "Radio-friendly hooks, steel guitar, polished vocals" },
      { label: "Red Dirt", description: "Texas/Oklahoma independent country, Turnpike Troubadours grit" },
      { label: "Outlaw Country", description: "Waylon/Willie rebel spirit meets rock edge" }
    ]
  },
  {
    name: "Acoustic & Folk",
    options: [
      { label: "Singer-Songwriter", description: "Focus on lyrics and intimate accompaniment" },
      { label: "Acoustic Guitar", description: "Pure wood-and-string unplugged textures" },
      { label: "Acoustic Piano", description: "Traditional keys or saloon upright vibes" },
      { label: "Unplugged", description: "Stripped-down, raw acoustic versions" },
      { label: "Acoustic Folk", description: "Traditional storytelling, fingerpicking, campfire warmth" },
      { label: "Celtic Folk", description: "Irish/Scottish fiddle, tin whistle, jig rhythms" },
      { label: "Nordic Folk", description: "Scandinavian nyckelharpa, haunting open-string drones" },
      { label: "Scottish Folk", description: "Bagpipes, bodhran, highland ballad melancholy" }
    ]
  },
  {
    name: "Hip-Hop & Rap",
    options: [
      { label: "Boom Bap", description: "Classic 90s sample-chop loops, SP-1200 grit, DJ Premier scratch hooks" },
      { label: "Trap", description: "808 sub-bass, rapid hi-hat rolls, dark melodic pads" },
      { label: "G-Funk", description: "Dr. Dre West Coast synth bass, talk box, Parliament grooves" },
      { label: "Drill", description: "Sliding 808 glides, aggressive hi-hats, dark UK/Chicago energy" },
      { label: "Lo-Fi Hip-Hop", description: "Vinyl crackle, detuned samples, mellow chill beats" },
      { label: "Jazz Rap", description: "Live jazz samples, Tribe Called Quest sophistication" },
      { label: "Old School Hip-Hop", description: "Breakbeats, turntablism, Run-DMC/Grandmaster Flash era" },
      { label: "Southern Hip-Hop", description: "Dirty South bounce, crunk energy, trunk-rattling bass" },
      { label: "Cloud Rap", description: "Ethereal pads, heavy reverb, spacey atmospheric production" },
      { label: "Conscious Rap", description: "Lyrical depth, socially aware, Kendrick/Common storytelling" }
    ]
  },
  {
    name: "EDM & Electronic",
    options: [
      { label: "House", description: "Four-on-the-floor kick, warm pads, Chicago/Ibiza grooves" },
      { label: "Deep House", description: "Soulful chords, sub-bass warmth, late-night sophistication" },
      { label: "Tech House", description: "Minimal percussion loops, rolling basslines, warehouse energy" },
      { label: "Techno", description: "Industrial rhythms, Berlin-style dark machine pulse" },
      { label: "Trance", description: "Euphoric builds, arpeggiated synths, emotional breakdowns" },
      { label: "Psytrance", description: "Goa-style acid lines, 140+ BPM, psychedelic squelches and spirals" },
      { label: "Dubstep", description: "Heavy wobble bass, half-time drums, Skrillex-era drops" },
      { label: "Drum & Bass", description: "Breakbeat-driven 170+ BPM, rolling basslines, jungle roots" },
      { label: "Synthwave", description: "80s retro neon, analog synth arpeggios, Kavinsky/Perturbator vibes" },
      { label: "Future Bass", description: "Lush supersaw chords, pitched vocals, Flume-style shimmer" },
      { label: "Ambient Electronic", description: "Atmospheric textures, Brian Eno-style soundscapes" },
      { label: "IDM", description: "Aphex Twin glitch, complex rhythms, experimental sound design" }
    ]
  },
  {
    name: "Pop & R&B",
    options: [
      { label: "Synth Pop", description: "Depeche Mode/Pet Shop Boys electronic pop hooks" },
      { label: "Indie Pop", description: "Lo-fi charm, jangly guitars, quirky melodies" },
      { label: "Dream Pop", description: "Shoegaze-adjacent, reverb-drenched ethereal vocals, Cocteau Twins" },
      { label: "Electropop", description: "Polished electronic production, Charli XCX/Robyn club-ready" },
      { label: "Dance Pop", description: "Radio-ready hooks, four-on-the-floor energy, Dua Lipa shine" },
      { label: "Art Pop", description: "Experimental pop, Kate Bush/Bjork boundary-pushing creativity" },
      { label: "Power Pop", description: "Big choruses, crunchy guitars, Cheap Trick/Weezer energy" },
      { label: "K-Pop", description: "Precision choreography pop, layered production, genre-blending" },
      { label: "R&B", description: "Smooth vocals, groove-driven, Usher/SZA modern soul" },
      { label: "Alternative R&B", description: "Frank Ocean/The Weeknd dark atmospherics, genre-fluid" }
    ]
  },
  {
    name: "Vocal & Classical",
    options: [
      { label: "A Cappella", description: "Pure unaccompanied vocal harmony and beatboxing" },
      { label: "Opera", description: "Classical vocal power, aria structure, orchestral drama" },
      { label: "Symphonic", description: "Full orchestra arrangement, cinematic grandeur" }
    ]
  }
];

export const BLEND_OPTIONS = []; // Now fully integrated into QUICK_BLEND_GROUPS